/**
 * One assignment's editable surface in the main column.
 *
 * Shows the assignment's scope subset (grouped, so scope is visible
 * without opening the full grid), the editable commercials
 * (AssignmentCommercialsForm), its attached files, and the "Network
 * partners" section: inline NP
 * search + shortlist chips. Clicking a chip (or the card header) makes
 * this the active assignment and opens that NP's conversation in the
 * right-side aside; the aside is chat-only.
 */

import { useState } from 'react';

import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Divider,
  Group,
  Loader,
  Modal,
  MultiSelect,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconCopy,
  IconFileExport,
  IconLock,
  IconLockOpen,
  IconSearch,
  IconTrash,
  IconX,
} from '@tabler/icons-react';

import { MessageAttachment } from '@/components/Chat';
import { AttachmentRemoveButton } from '@/components/Chat/MessageAttachment';
import { useNetworkPartnerSearch } from '@/hooks/useNetworkPartners';
import { vnextApiClient } from '@/services/vnext-api';
import type { ConversationAttachment } from '@/types/attachment';
import { formatNpLocation, NP_STATUS_META } from '@/types/network-partner';
import {
  isAssignmentCancelled,
  isManagedAssignment,
  type AssignmentFile,
  type BackofficeAssignment,
  type NpShortlistEntry,
} from '@/types/np-assignments';
import { AssignmentCommercialsForm } from './AssignmentCommercialsForm';
import { CancelAssignmentControl } from './CancelAssignmentControl';
import { CancelledNote } from './CancelledNote';
import { ManagedAssignmentCard } from './ManagedAssignmentCard';
import { useNpAssignmentsContext } from './NpAssignmentsContext';
import { ManualRegistryOrders } from './RegistryOrdersBilled';
import {
  lockConfirmLabel,
  RegistryTakeoverNotice,
  registryTakeoverOf,
} from './RegistryTakeoverNotice';

const STATUS_LABEL: Record<string, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'gray' },
  shortlisting: { label: 'Shortlisting', color: 'blue' },
  np_locked: { label: 'NP locked', color: 'green' },
  sent_to_backoffice: { label: 'Sent to backoffice', color: 'teal' },
};

/** Adapt an AssignmentFile to the ConversationAttachment shape that
 *  MessageAttachment renders (only the fields it reads). */
function asConversationAttachment(f: AssignmentFile): ConversationAttachment {
  return {
    id: f.conversationAttachmentId,
    chatThreadId: '',
    chatMessageId: null,
    parentAttachmentId: null,
    source: 'analyst_upload',
    classification: 'internal',
    filename: f.filename ?? 'file',
    contentType: f.contentType,
    sizeBytes: f.sizeBytes ?? 0,
    sha256: null,
    freshdeskAttachmentId: null,
    createdByUserId: f.createdByUserId,
    createdAt: f.createdAt,
    downloadUrl: f.downloadUrl,
  };
}

interface AssignmentCardProps {
  assignment: BackofficeAssignment;
  index: number;
}

/** Armadillo's automatic assignment is kept by the report's registry orders
 * and gets a read-only card; every other assignment is edited here. */
export function AssignmentCard(props: Readonly<AssignmentCardProps>) {
  return isManagedAssignment(props.assignment) ? (
    <ManagedAssignmentCard {...props} />
  ) : (
    <EditableAssignmentCard {...props} />
  );
}

function EditableAssignmentCard({ assignment, index }: Readonly<AssignmentCardProps>) {
  const {
    actions,
    activeAssignmentId,
    setActiveAssignmentId,
    selectAssignment,
    colorForAssignment,
    reportStarted,
  } = useNpAssignmentsContext();
  const active = activeAssignmentId === assignment.id;
  const dispatched = assignment.status === 'sent_to_backoffice';
  const color = colorForAssignment(assignment.id);
  const cancelled = isAssignmentCancelled(assignment);
  const statusMeta = cancelled
    ? { label: 'Cancelled', color: 'gray' }
    : (STATUS_LABEL[assignment.status] ?? { label: assignment.status, color: 'gray' });

  const select = () => {
    selectAssignment(assignment.id, assignment.networkPartnerId ?? null);
  };

  return (
    <Card
      withBorder
      padding="md"
      radius="md"
      data-testid={`assignment-card-${assignment.id}`}
      style={{
        borderLeft: `4px solid var(--mantine-color-${cancelled ? 'gray' : color}-6)`,
        opacity: cancelled ? 0.75 : undefined,
        outline: active ? '2px solid var(--mantine-color-blue-4)' : undefined,
      }}
    >
      <Stack gap="sm">
        <Group
          justify="space-between"
          align="flex-start"
          onClick={select}
          style={{ cursor: 'pointer' }}
        >
          <Group gap="xs">
            <Badge color={color} variant="filled">
              A{index + 1}
            </Badge>
            <Text fw={600}>{assignment.items.length} item(s)</Text>
          </Group>
          <Group gap="xs">
            <Badge color={statusMeta.color} variant="light">
              {statusMeta.label}
            </Badge>
            <Tooltip
              label={
                reportStarted
                  ? 'Add another partner (same scope, new partner)'
                  : 'Duplicate assignment (same scope, new partner)'
              }
            >
              <ActionIcon
                variant="subtle"
                onClick={(e) => {
                  e.stopPropagation();
                  void actions.duplicate(assignment.id);
                }}
                aria-label="Duplicate assignment"
              >
                <IconCopy size={16} />
              </ActionIcon>
            </Tooltip>
            <CancelAssignmentControl assignment={assignment} />
            {!dispatched && (
              <Tooltip label="Delete assignment">
                <ActionIcon
                  variant="subtle"
                  color="red"
                  onClick={(e) => {
                    e.stopPropagation();
                    void actions.remove(assignment.id);
                    if (active) {
                      setActiveAssignmentId(null);
                    }
                  }}
                  aria-label="Delete assignment"
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        </Group>

        <CancelledNote assignment={assignment} />

        <ScopeSummary assignment={assignment} dispatched={dispatched} />

        <Divider />
        <AssignmentCommercialsForm assignment={assignment} dispatched={dispatched} />
        <ManualRegistryOrders assignment={assignment} />

        <Divider />
        <FilesSection assignment={assignment} dispatched={dispatched} />

        <Divider />
        <NetworkPartnersSection assignment={assignment} />

        {reportStarted && !dispatched && (
          <>
            <Divider />
            <LateDispatch assignment={assignment} />
          </>
        )}
      </Stack>
    </Card>
  );
}

/** Sends one assignment of a report that has already started. The request is
 * created and activated at once, so the partner is assigned as soon as it is
 * sent — a partner added late. */
function LateDispatch({ assignment }: Readonly<{ assignment: BackofficeAssignment }>) {
  const { actions } = useNpAssignmentsContext();
  const [sending, setSending] = useState(false);
  const ready = assignment.isReady;

  const send = async () => {
    setSending(true);
    try {
      await actions.dispatch(assignment.id);
    } finally {
      setSending(false);
    }
  };

  return (
    <Stack gap={4}>
      <Button
        size="xs"
        onClick={() => void send()}
        disabled={!ready}
        loading={sending}
        data-testid={`dispatch-assignment-${assignment.id}`}
      >
        Dispatch to Backoffice
      </Button>
      <Text size="xs" c="dimmed">
        {ready
          ? 'The report has started, so the partner is assigned as soon as this is sent.'
          : 'Lock a partner and set a fee and a deadline to dispatch.'}
      </Text>
    </Stack>
  );
}

function ScopeSummary({
  assignment,
  dispatched,
}: Readonly<{ assignment: BackofficeAssignment; dispatched: boolean }>) {
  const { actions } = useNpAssignmentsContext();
  return (
    <Stack gap={4}>
      <Text size="xs" c="dimmed" fw={500}>
        Scope
      </Text>
      {assignment.items.map((it) => (
        <Group key={it.itemId} gap="xs" wrap="nowrap" justify="space-between">
          <Text size="sm" truncate>
            {it.scopeComponentSlug}
            {it.subjectName ? (
              <Text span c="dimmed">
                {' '}
                · {it.subjectName}
              </Text>
            ) : null}
            {it.jurisdictionName ? (
              <Text span c="dimmed">
                {' '}
                · {it.jurisdictionName}
              </Text>
            ) : null}
          </Text>
          {!dispatched && assignment.items.length > 1 && (
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              aria-label="Remove item from assignment"
              onClick={() => void actions.removeItem(assignment.id, it.itemId)}
            >
              <IconX size={14} />
            </ActionIcon>
          )}
        </Group>
      ))}
    </Stack>
  );
}

function FilesSection({
  assignment,
  dispatched,
}: Readonly<{ assignment: BackofficeAssignment; dispatched: boolean }>) {
  const { actions } = useNpAssignmentsContext();
  // Candidate files come from the conversation attachments already bound
  // to the RR (client submissions + chat uploads) — a dropdown, not a
  // paste-the-uuid field. Files already on this assignment are excluded.
  const { candidateFiles } = useNpAssignmentsContext();
  const [pendingIds, setPendingIds] = useState<string[]>([]);

  const attachedIds = new Set(assignment.attachments.map((f) => f.conversationAttachmentId));
  const options = candidateFiles
    .filter((f) => !attachedIds.has(f.conversationAttachmentId))
    .map((f) => ({
      value: f.conversationAttachmentId,
      label: f.filename ?? f.conversationAttachmentId,
    }));

  const handleAttach = async () => {
    if (pendingIds.length === 0) {
      return;
    }
    const ids = pendingIds;
    setPendingIds([]);
    // Sequential: attachFile refetches the assignment list per call, so
    // parallel calls would race the refresh.
    for (const id of ids) {
      await actions.attachFile(assignment.id, id);
    }
  };

  return (
    <Stack gap={4}>
      <Text size="xs" c="dimmed" fw={500}>
        Project documents ({assignment.attachments.length})
      </Text>
      {assignment.attachments.map((f) => (
        <MessageAttachment
          key={f.id}
          attachment={asConversationAttachment(f)}
          compact
          action={
            dispatched ? undefined : (
              <AttachmentRemoveButton
                onRemove={() => void actions.detachFile(assignment.id, f.conversationAttachmentId)}
              />
            )
          }
        />
      ))}
      {!dispatched && (
        <Group gap="xs" align="flex-end" wrap="nowrap">
          <MultiSelect
            size="xs"
            style={{ flex: 1 }}
            label="Attach conversation files"
            placeholder={options.length ? 'Pick files…' : 'No conversation files available'}
            data={options}
            value={pendingIds}
            onChange={setPendingIds}
            searchable
            clearable
            disabled={options.length === 0}
          />
          <Button
            size="xs"
            variant="subtle"
            data-testid="attach-files-select-all"
            disabled={options.length === 0 || pendingIds.length === options.length}
            onClick={() => setPendingIds(options.map((o) => o.value))}
          >
            Select all
          </Button>
          <Button
            size="xs"
            variant="default"
            disabled={pendingIds.length === 0}
            onClick={() => void handleAttach()}
          >
            Attach{pendingIds.length > 1 ? ` ${pendingIds.length}` : ''}
          </Button>
        </Group>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Network partners: inline search + shortlist chips (the aside is chat-only)
// ---------------------------------------------------------------------------

function NetworkPartnersSection({ assignment }: Readonly<{ assignment: BackofficeAssignment }>) {
  const dispatched = assignment.status === 'sent_to_backoffice';

  // Chips = the shortlist, plus the locked NP if it was never shortlisted
  // (possible on older assignments) so the lock is always visible.
  const chips: NpShortlistEntry[] = [...assignment.shortlist];
  if (
    assignment.networkPartnerId &&
    assignment.networkPartner &&
    !chips.some((s) => s.npId === assignment.networkPartnerId)
  ) {
    chips.push({
      npId: assignment.networkPartnerId,
      networkPartner: assignment.networkPartner,
      threadId: null,
      lastMessageAt: null,
      previewNpRequestId: null,
      previewSharedAt: null,
    });
  }

  return (
    <Stack gap={6}>
      <Text size="xs" c="dimmed" fw={500}>
        Network partners
      </Text>
      {chips.length > 0 && (
        <Group gap={6}>
          {chips.map((entry) => (
            <ShortlistChip
              key={entry.npId}
              assignment={assignment}
              entry={entry}
              dispatched={dispatched}
            />
          ))}
        </Group>
      )}
      {chips.length === 0 && (
        <Text size="xs" c="dimmed">
          Search below and add partners to the shortlist, then agree terms over chat.
        </Text>
      )}
      {!dispatched && <NpSearchInline assignment={assignment} />}
    </Stack>
  );
}

function ShortlistChip({
  assignment,
  entry,
  dispatched,
}: Readonly<{
  assignment: BackofficeAssignment;
  entry: NpShortlistEntry;
  dispatched: boolean;
}>) {
  const { actions, selectAssignment } = useNpAssignmentsContext();
  const isLocked = assignment.networkPartnerId === entry.npId;
  const name = entry.networkPartner.name ?? entry.networkPartner.email ?? entry.npId.slice(0, 8);

  return (
    <Group
      gap={4}
      wrap="nowrap"
      style={{
        border: `1px solid var(--mantine-color-${isLocked ? 'green' : 'gray'}-4)`,
        borderRadius: 'var(--mantine-radius-xl)',
        paddingBlock: 2,
        paddingInline: '10px 6px',
      }}
    >
      <UnstyledButton
        onClick={() => selectAssignment(assignment.id, entry.npId)}
        data-testid={`shortlist-chip-${entry.npId}`}
        aria-label={`Open chat with ${name}`}
      >
        <Group gap={6} wrap="nowrap">
          <Text size="xs" fw={500}>
            {name}
          </Text>
          {isLocked && (
            <Badge color="green" size="xs" variant="filled">
              Locked
            </Badge>
          )}
          {!isLocked && entry.networkPartner.status !== 'active' && (
            <Badge
              color={NP_STATUS_META[entry.networkPartner.status]?.color ?? 'gray'}
              size="xs"
              variant="light"
            >
              {NP_STATUS_META[entry.networkPartner.status]?.label ?? entry.networkPartner.status}
            </Badge>
          )}
        </Group>
      </UnstyledButton>
      {/* Docs shared with this candidate (pre-lock preview) — the share/
          withdraw control lives in the NP chat aside, next to Lock in. */}
      {entry.previewNpRequestId !== null && (
        <Tooltip label={`Docs shared with ${name} — withdraw from the chat aside`}>
          <IconFileExport
            size={12}
            style={{ color: 'var(--mantine-color-blue-6)' }}
            aria-label={`Docs shared with ${name}`}
            data-testid={`shortlist-chip-shared-${entry.npId}`}
          />
        </Tooltip>
      )}
      {/* Lock-in mirrored on the card (same confirm as the chat aside) so
          the decision isn't buried in the 400px aside. */}
      {!dispatched && !isLocked && (
        <LockInControl assignment={assignment} entry={entry} name={name} />
      )}
      {!dispatched && isLocked && (
        <Tooltip label={`Unlock ${name}`}>
          <ActionIcon
            size="xs"
            variant="subtle"
            color="gray"
            aria-label={`Unlock ${name}`}
            data-testid={`shortlist-chip-unlock-${entry.npId}`}
            onClick={() => void actions.unlockNp(assignment.id)}
          >
            <IconLockOpen size={12} />
          </ActionIcon>
        </Tooltip>
      )}
      {/* The locked NP can't be removed from the shortlist — unlock first. */}
      {!dispatched && !isLocked && (
        <ActionIcon
          size="xs"
          variant="subtle"
          color="gray"
          aria-label={`Remove ${name} from shortlist`}
          data-testid={`shortlist-chip-remove-${entry.npId}`}
          onClick={() => void actions.removeShortlistNp(assignment.id, entry.npId)}
        >
          <IconX size={12} />
        </ActionIcon>
      )}
    </Group>
  );
}

/**
 * Chip-level "Lock in" — the same confirm modal semantics as the chat
 * aside (fee/due nudge; prospects are activated first, D10), so locking
 * no longer requires opening the aside at all.
 */
function LockInControl({
  assignment,
  entry,
  name,
}: Readonly<{
  assignment: BackofficeAssignment;
  entry: NpShortlistEntry;
  name: string;
}>) {
  const { actions, assignments } = useNpAssignmentsContext();
  const [confirmOpen, { open: openConfirm, close: closeConfirm }] = useDisclosure(false);
  const isProspect = entry.networkPartner.status === 'prospect';
  // Locking Armadillo here takes over the report's automatic assignment.
  const takeover = registryTakeoverOf(assignments, assignment, entry.networkPartner);

  const handleLock = () => {
    void (async () => {
      if (isProspect) {
        try {
          await vnextApiClient.updateNetworkPartner(entry.npId, { status: 'active' });
        } catch (err) {
          notifications.show({
            title: 'Could not activate partner',
            message: err instanceof Error ? err.message : String(err),
            color: 'red',
          });
          return;
        }
      }
      await actions.lockNp(assignment.id, entry.npId);
    })();
    closeConfirm();
  };

  return (
    <>
      <Tooltip label={`Lock in ${name}`}>
        <ActionIcon
          size="xs"
          variant="subtle"
          color="green"
          aria-label={`Lock in ${name}`}
          data-testid={`shortlist-chip-lock-${entry.npId}`}
          onClick={openConfirm}
        >
          <IconLock size={12} />
        </ActionIcon>
      </Tooltip>
      <Modal opened={confirmOpen} onClose={closeConfirm} title="Lock in Network Partner">
        <Stack gap="sm">
          {assignment.npFee == null ||
          (assignment.npDueDate == null && assignment.turnaroundTimeDays == null) ? (
            <Alert color="yellow">
              Set a fee and a due date (or turnaround) on the assignment so it counts as ready — you
              can still lock the NP in now.
            </Alert>
          ) : null}
          {isProspect && (
            <Alert color="yellow">
              {name} is a prospect — locking in will activate them first.
            </Alert>
          )}
          {takeover && (
            <RegistryTakeoverNotice
              assignments={assignments}
              managed={takeover}
              target={assignment}
            />
          )}
          <Text size="sm">
            Lock in <strong>{name}</strong> for this assignment
            {assignment.npFee != null ? ` at ${assignment.npFee}` : ''}
            {assignment.npDueDate
              ? `, due ${assignment.npDueDate}`
              : assignment.turnaroundTimeDays != null
                ? `, ${assignment.turnaroundTimeDays} day turnaround`
                : ''}
            ?
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={closeConfirm}>
              Cancel
            </Button>
            <Button color="green" onClick={handleLock} data-testid="lock-in-confirm">
              {lockConfirmLabel(isProspect, takeover !== null)}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

function NpSearchInline({ assignment }: Readonly<{ assignment: BackofficeAssignment }>) {
  const { actions, selectAssignment } = useNpAssignmentsContext();
  const [query, setQuery] = useState('');
  const { items, loading } = useNetworkPartnerSearch(query);
  const existing = new Set(assignment.shortlist.map((s) => s.npId));

  return (
    <Stack gap={4}>
      <TextInput
        size="xs"
        placeholder="Search Network Partners…"
        leftSection={<IconSearch size={14} />}
        value={query}
        onChange={(e) => setQuery(e.currentTarget.value)}
        data-testid={`np-search-${assignment.id}`}
      />
      {loading && <Loader size="xs" />}
      {query.trim() && items.length > 0 && (
        <Card withBorder padding="xs" radius="sm">
          <ScrollArea.Autosize mah={160}>
            <Stack gap={2}>
              {items.map((np) => (
                <Group key={np.id} justify="space-between" wrap="nowrap">
                  <Stack gap={0} style={{ minWidth: 0 }}>
                    <Group gap={6} wrap="nowrap">
                      <Text size="sm" truncate>
                        {np.name ?? np.email ?? np.id}
                      </Text>
                      <Badge
                        size="xs"
                        variant="light"
                        color={NP_STATUS_META[np.status]?.color ?? 'gray'}
                      >
                        {NP_STATUS_META[np.status]?.label ?? np.status}
                      </Badge>
                    </Group>
                    {(np.company || formatNpLocation(np)) && (
                      <Text size="xs" c="dimmed" truncate>
                        {[np.company, formatNpLocation(np)].filter(Boolean).join(' · ')}
                      </Text>
                    )}
                    {/* Email only when the name row isn't already it. */}
                    {np.name && np.email && (
                      <Text size="xs" c="dimmed" truncate>
                        {np.email}
                      </Text>
                    )}
                  </Stack>
                  <Button
                    size="compact-xs"
                    variant="light"
                    disabled={existing.has(np.id)}
                    data-testid={`np-search-add-${np.id}`}
                    onClick={() => {
                      void actions.addShortlistNp(assignment.id, np.id);
                      // Open the conversation straight away — the analyst's
                      // next step is always to message the new partner.
                      selectAssignment(assignment.id, np.id);
                      setQuery('');
                    }}
                  >
                    {existing.has(np.id) ? 'Added' : 'Add'}
                  </Button>
                </Group>
              ))}
            </Stack>
          </ScrollArea.Autosize>
        </Card>
      )}
    </Stack>
  );
}
